import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { CeRequestStateService } from '../../services/ce-request-state.service';
import { FlowStep, getQuestionnaireSteps } from '../../data/flow-steps';
import { HasUnsavedChanges } from '../../guards/unsaved-changes.guard';
import { QuestionnaireApiService } from '../../services/questionnaire-api.service';
import { ProjectsApiService } from '../../services/projects-api.service';
import { CurrentUserService } from '../../services/current-user.service';
import { DictionariesApiService } from '../../services/dictionaries-api.service';
import { LoggerService } from '../../services/logger.service';
import { ProjectStageService } from '../../services/project-stage.service';
import { AnswerDto, AnswerViewModel, QuestionnaireViewModel, UpdateAnswerDto } from '../../models/questionnaire-api.model';

const SUBMITTED_STATUS_TYPE = 1;
const ISSUED_STATUS_TYPE = 4;
const DRAFT_STATUS_TYPE = 0;
const PENDING_ADDITIONAL_INFORMATION_STATUS_TYPE = 5;

interface QuestionnaireUiItem {
  id: number;
  text: string;
  comment: string;
  fileName: string;
  isMandatory: boolean;
  hasAttachment: boolean;
  isMandatoryAttachment: boolean;
  attachmentPath: string;
  displayNumber: number;
  groupName: string | null;
}

interface CommentSegment {
  text: string;
  href: string | null;
}

@Component({
  selector: 'app-questionnaire',
  standalone: true,
  imports: [FormsModule, MatIconModule],
  templateUrl: './questionnaire.html',
  styleUrl: './questionnaire.scss',
})
export class QuestionnaireComponent implements HasUnsavedChanges {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly state = inject(CeRequestStateService);
  private readonly questionnaireApi = inject(QuestionnaireApiService);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly dictionariesApi = inject(DictionariesApiService);
  private readonly logger = inject(LoggerService);
  private readonly stageService = inject(ProjectStageService);

  protected readonly steps = computed(() =>
    getQuestionnaireSteps(
      this.state.draftForm().isNewProject,
      this.state.flowProgress().responseVisited,
      this.canAccessResponse(),
    ),
  );
  protected readonly isProjectEngineer = computed(() => this.currentUserService.hasAnyRole(['project_engineer', 'project_engineers']));
  protected readonly canAccessResponse = computed(() => {
    if (!this.isProjectEngineer()) {
      return true;
    }

    const status = this.projectStatusType();
    return typeof status === 'number' && status >= SUBMITTED_STATUS_TYPE;
  });

  protected readonly draft = computed(() => this.state.draftForm());
  protected readonly formattedDesignProgress = computed(() => {
    const value = this.draft().designProgress?.trim();
    return value || '-';
  });
  private readonly projectClassNumber = signal<string | null>(null);

  protected readonly hasValidationErrors = computed(() => {
    if (!this.submitAttempted()) {
      return false;
    }
    return this.questions().some(q =>
      (q.isMandatory && !q.comment.trim()) ||
      this.isMandatoryAttachmentMissingBeforeUpload(q),
    );
  });

  protected readonly groupedQuestions = computed(() => {
    const questions = this.questions();
    const groups: Array<{ groupName: string | null; questions: QuestionnaireUiItem[] }> = [];

    for (const question of questions) {
      const existingGroup = groups.find(g => g.groupName === question.groupName);
      if (existingGroup) {
        existingGroup.questions.push(question);
      } else {
        groups.push({ groupName: question.groupName, questions: [question] });
      }
    }

    return groups;
  });

  protected readonly questions = signal<QuestionnaireUiItem[]>([]);
  protected readonly classNumber = signal<string | null>(null);
  protected readonly projectId = signal<number | null>(null);
  protected readonly projectStatusType = signal<number | null>(null);
  protected readonly readonlyMode = signal(false);
  protected readonly isSubmittedStatus = computed(() => {
    const status = this.projectStatusType();
    return status === SUBMITTED_STATUS_TYPE || status === ISSUED_STATUS_TYPE;
  });
  protected readonly isReadonlyQuestionnaire = computed(() => this.isSubmittedStatus() || this.readonlyMode());
  protected readonly isLoading = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly apiError = signal<string | null>(null);
  protected readonly saveMessage = signal<string | null>(null);
  protected readonly submitAttempted = signal(false);
  private readonly hasExistingAnswers = signal(false);
  private readonly isDirty = signal(false);

  protected readonly shouldShowSaveDraft = computed(() => {
    // Save as Draft skips mandatory-field validation and is available only while the project is in Draft status.
    return !!this.projectId() && this.projectStatusType() === DRAFT_STATUS_TYPE && !this.isReadonlyQuestionnaire();
  });

  hasUnsavedChanges(): boolean {
    return !this.isReadonlyQuestionnaire() && this.isDirty();
  }

  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  protected readonly shouldShowSave = computed(() => {
    // Validated Save is for existing projects outside Draft status (Draft uses Save as Draft instead).
    return !!this.projectId() && this.projectStatusType() !== DRAFT_STATUS_TYPE;
  });

  protected readonly shouldShowSubmitRequest = computed(() => {
    const id = this.projectId();
    const status = this.projectStatusType();

    // Submit is allowed only for existing projects.
    if (!id || status === null) {
      return false;
    }

    if (status === DRAFT_STATUS_TYPE) {
      return true;
    }

    if (status === PENDING_ADDITIONAL_INFORMATION_STATUS_TYPE) {
      return this.isProjectEngineer();
    }

    return false;
  });

  protected readonly shouldShowGoToResponseButton = computed(() => {
    return !this.shouldShowSubmitRequest() && this.canAccessResponse();
  });
  private answerIdByQuestionId = new Map<number, number>();
  private readonly pendingFiles = new Map<number, File>();
  private readonly pendingAttachmentDeletes = new Set<string>();
  private lastPersistRequestType: 'none' | 'post' | 'put' = 'none';

  protected hasCommentLinks(comment: string): boolean {
    return this.toCommentSegments(comment).some(segment => !!segment.href);
  }

  protected getCommentSegments(comment: string): readonly CommentSegment[] {
    return this.toCommentSegments(comment);
  }

  private toCommentSegments(comment: string): CommentSegment[] {
    const source = comment ?? '';
    if (!source) {
      return [{ text: '', href: null }];
    }

    const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const segments: CommentSegment[] = [];
    let lastIndex = 0;

    for (const match of source.matchAll(regex)) {
      const raw = match[0] ?? '';
      const start = match.index ?? 0;

      if (start > lastIndex) {
        segments.push({ text: source.slice(lastIndex, start), href: null });
      }

      const [urlText, trailing] = this.trimTrailingPunctuation(raw);
      const href = this.normalizeUrl(urlText);

      segments.push({ text: urlText, href });
      if (trailing) {
        segments.push({ text: trailing, href: null });
      }

      lastIndex = start + raw.length;
    }

    if (lastIndex < source.length) {
      segments.push({ text: source.slice(lastIndex), href: null });
    }

    return segments.length ? segments : [{ text: source, href: null }];
  }

  private trimTrailingPunctuation(value: string): [string, string] {
    const match = value.match(/^(.+?)([.,!?;:)]*)$/);
    if (!match) {
      return [value, ''];
    }

    const main = match[1] ?? value;
    const trailing = match[2] ?? '';
    return [main, trailing];
  }

  private normalizeUrl(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const parsed = new URL(withProtocol);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null;
      }
      return parsed.toString();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  constructor() {
    void this.currentUserService.load();
    this.state.markQuestionnaireVisited();
    void this.initializeQuestionnaire();
  }

  protected goBack(): void {
    const previous = this.state.draftForm().isNewProject ? '/new-project' : '/existing-project';
    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate([previous], queryParams ? { queryParams } : {});
  }

  protected onCommentChange(questionId: number, comment: string): void {
    if (this.isReadonlyQuestionnaire()) {
      return;
    }

    this.isDirty.set(true);
    this.questions.update(list =>
      list.map(item =>
        item.id === questionId
          ? {
              ...item,
              comment,
            }
          : item,
      ),
    );
  }

  protected onFileSelected(questionId: number, event: Event): void {
    if (this.isReadonlyQuestionnaire()) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const fileName = file?.name ?? '';

    if (file) {
      this.pendingFiles.set(questionId, file);
      this.isDirty.set(true);
    }

    this.questions.update(list =>
      list.map(item =>
        item.id === questionId
          ? {
              ...item,
              fileName,
              attachmentPath: '',
            }
          : item,
      ),
    );
  }

  protected async removeAttachment(questionId: number, displayNumber: number): Promise<void> {
    if (this.isReadonlyQuestionnaire()) {
      return;
    }

    const question = this.questions().find(item => item.id === questionId);
    const normalizedAttachmentPath = this.questionnaireApi.normalizeAttachmentPath(question?.attachmentPath);
    if (normalizedAttachmentPath) {
      // Defer physical file deletion until answers are successfully persisted.
      this.pendingAttachmentDeletes.add(normalizedAttachmentPath);
    }

    this.pendingFiles.delete(questionId);
    this.isDirty.set(true);

    const input = document.getElementById(`file-${displayNumber}`) as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }

    this.questions.update(list =>
      list.map(item =>
        item.id === questionId
          ? {
              ...item,
              fileName: '',
              attachmentPath: '',
            }
          : item,
      ),
    );
  }

  protected async downloadFile(attachmentPath: string, fileName?: string): Promise<void> {
    try {
      const normalizedAttachmentPath = this.questionnaireApi.normalizeAttachmentPath(attachmentPath);
      const blob = await this.questionnaireApi.downloadAttachment(normalizedAttachmentPath);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const normalizedFileName = (fileName ?? '').trim();
      link.download = normalizedFileName || this.getAttachmentFileName(normalizedAttachmentPath);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      this.apiError.set('Failed to download file.');
    }
  }

  protected isStepClickable(step: FlowStep): boolean {
    return !!(step.completed && step.route);
  }

  protected formatQuestionNumber(displayNumber: number): string {
    return String(displayNumber).padStart(2, '0');
  }

  protected isQuestionValid(question: QuestionnaireUiItem): boolean {
    return !this.isCommentMissing(question) && !this.isAttachmentMissing(question);
  }

  protected isCommentMissing(question: QuestionnaireUiItem): boolean {
    if (!this.submitAttempted()) {
      return false;
    }
    if (!question.isMandatory) {
      return false;
    }

    return !question.comment.trim();
  }

  protected isAttachmentMissing(question: QuestionnaireUiItem): boolean {
    if (!this.submitAttempted()) {
      return false;
    }
    return this.isMandatoryAttachmentMissingBeforeUpload(question);
  }

  protected goToStep(step: FlowStep): void {
    if (!this.isStepClickable(step)) {
      return;
    }

    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate([step.route!], queryParams ? { queryParams } : {});
  }

  protected async saveDraft(): Promise<void> {
    if (!this.shouldShowSaveDraft()) {
      return;
    }

    // A draft intentionally skips mandatory comment/attachment validation.
    this.submitAttempted.set(false);

    const saved = await this.persistAnswers({ draft: true });
    if (!saved) {
      if (!this.apiError()) {
        this.apiError.set('Failed to save questionnaire answers.');
      }
      return;
    }

    this.saveMessage.set(
      this.pendingFiles.size
        ? 'Draft saved, but some attachments failed to upload. Please attach them again.'
        : 'Draft saved. You can continue editing it later.',
    );
  }

  protected async save(): Promise<void> {
    if (this.isReadonlyQuestionnaire()) {
      return;
    }

    this.submitAttempted.set(true);
    if (this.hasValidationErrors()) {
      this.apiError.set('Please fill in all mandatory questions.');
      this.focusFirstInvalidField();
      return;
    }

    const saved = await this.persistAnswers();
    if (!saved) {
      return;
    }

    if (this.lastPersistRequestType === 'post') {
      window.location.reload();
    }
  }

  protected async submitRequest(): Promise<void> {
    if (this.isReadonlyQuestionnaire()) {
      return;
    }

    this.submitAttempted.set(true);
    if (this.hasValidationErrors()) {
      this.apiError.set('Please fill in all mandatory questions.');
      this.focusFirstInvalidField();
      return;
    }

    const answersSaved = await this.persistAnswers();
    if (!answersSaved) {
      this.apiError.set('Failed to save questionnaire answers.');
      return;
    }

    const projectId = this.projectId();
    if (!projectId) {
      this.apiError.set('Project ID is required to submit CE request.');
      return;
    }

    const submitted = await this.submitProject(projectId);
    if (!submitted) {
      this.apiError.set('Failed to submit project.');
      return;
    }

    this.router.navigate(['/view-request'], { queryParams: { projectId } });
  }

  protected goToResponsePage(): void {
    if (!this.canAccessResponse()) {
      return;
    }

    const projectId = this.projectId();
    if (projectId) {
      this.router.navigate(['/response-page'], { queryParams: { projectId } });
      return;
    }

    this.router.navigate(['/response-page']);
  }

  private async initializeQuestionnaire(): Promise<void> {
    const readonlyRaw = this.route.snapshot.queryParamMap.get('readonly')?.trim().toLowerCase();
    this.readonlyMode.set(readonlyRaw === 'true' || readonlyRaw === '1' || readonlyRaw === 'yes');

    const projectIdRaw = this.route.snapshot.queryParamMap.get('projectId');
    const parsedProjectId = projectIdRaw ? Number(projectIdRaw) : NaN;

    if (Number.isInteger(parsedProjectId) && parsedProjectId > 0) {
      this.projectId.set(parsedProjectId);
      // Load project details first so draftForm fields and classNumber are correct
      await this.loadProjectDetails(parsedProjectId);
    }

    try {
      await this.stageService.ensureLoaded();
    } catch (error) {
      this.logger.warn('Failed to load project stages for questionnaire', error);
    }

    // Class of estimate is always resolved by the backend (project DTO / project management type dictionary).
    const classNumber =
      this.projectClassNumber() ||
      this.stageService.getClassNumber(this.state.draftForm().projectManagementType) ||
      this.route.snapshot.queryParamMap.get('classNumber')?.trim() ||
      null;
    this.classNumber.set(classNumber);

    if (!classNumber) {
      this.apiError.set('Class of estimate could not be determined for this project.');
      return;
    }

    this.isLoading.set(true);
    this.apiError.set(null);

    try {
      const isIssuedProject = this.projectId() !== null && this.projectStatusType() === ISSUED_STATUS_TYPE;
      if (isIssuedProject) {
        await this.hydrateAnswers();
        if (!this.questions().length) {
          this.apiError.set('No questionnaire answers were found for this Issued project.');
        }
        return;
      }

      const questionnaire = await firstValueFrom(this.questionnaireApi.getQuestionnaire(classNumber));
      const mappedQuestions = this.mapQuestionnaire(questionnaire);
      this.questions.set(mappedQuestions);
      if (!mappedQuestions.length) {
        this.apiError.set('No questionnaire items returned for this class of estimate.');
      }
      await this.hydrateAnswers();
    } catch (error) {
      console.error(error);
      this.apiError.set('Questionnaire API is unavailable.');
      this.questions.set([]);
    } finally {
      this.isLoading.set(false);
      this.isDirty.set(false);
    }
  }

  private focusFirstInvalidField(): void {
    for (const question of this.questions()) {
      if (this.isCommentMissing(question)) {
        this.focusElementById(`comment-${question.displayNumber}`);
        return;
      }

      if (this.isAttachmentMissing(question)) {
        this.focusElementById(`attachment-trigger-${question.displayNumber}`);
        return;
      }
    }
  }

  private focusElementById(id: string): void {
    const element = document.getElementById(id) as HTMLElement | null;
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.focus();
  }

  private async loadProjectDetails(id: number): Promise<void> {
    try {
      const dto = await firstValueFrom(this.projectsApi.getProjectById(id));
      this.projectStatusType.set(dto.projectStatusType ?? 0);
      this.projectClassNumber.set(dto.classNumber ?? dto.classOfEstimate ?? null);
      this.state.draftForm.update(d => ({
        ...d,
        projectTeam: dto.projectTeam ?? '',
        afeNumber: dto.afeNumber ?? '',
        projectTitle: dto.projectTitle ?? '',
        estimateTitle: dto.estimateTitle ?? '',
        projectNumber: dto.projectNumber ?? '',
        projectCode: dto.projectCode ?? '',
        estimateRequestType: dto.estimateRequestType ?? '',
        projectManagementType: dto.projectManagementType ?? '',
        cpdepPhase: dto.cpdepPhase ?? '',
        designProgress: dto.designProgress ?? '',
        anticipatedCompletionDate: dto.projectCompletionDate?.split('T')[0] ?? '',
        preliminaryCostDeadline: dto.deadlineDate?.split('T')[0] ?? '',
        area: dto.area ?? '',
        unit: dto.unit ?? '',
        ibp: dto.ibpYear ?? '',
        taYear: dto.turnaroundYear ?? '',
        isNewProject: dto.isNewProject ?? d.isNewProject,
      }));
    } catch (error) {
      // Log header load errors; fields will remain empty
      this.logger.warn('Failed to load project header fields for questionnaire', error);
    }
  }

  private mapQuestionnaire(response: QuestionnaireViewModel): QuestionnaireUiItem[] {
    const result: QuestionnaireUiItem[] = [];
    let displayNumber = 1;

    for (const group of response.questionGroups ?? []) {
      for (const question of group.questions ?? []) {
        result.push({
          id: question.id,
          text: question.description ?? `Question ${question.id}`,
          isMandatory: question.isMandatory,
          hasAttachment: question.hasAttachment,
          isMandatoryAttachment: question.isMandatoryAttachment ?? (question.isMandatory && question.hasAttachment),
          comment: '',
          fileName: '',
          attachmentPath: '',
          displayNumber: displayNumber++,
          groupName: group.groupName ?? null,
        });
      }
    }

    return result;
  }

  private async hydrateAnswers(): Promise<void> {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }

    try {
      const answers = await firstValueFrom(this.questionnaireApi.getAnswersByProject(projectId));
      this.hasExistingAnswers.set(answers.length > 0);
      if (this.projectStatusType() === ISSUED_STATUS_TYPE) {
        this.applyIssuedAnswers(answers);
      } else {
        this.applyAnswers(answers);
      }
    } catch (error) {
      console.error(error);
      this.apiError.set('Questionnaire loaded, but existing answers could not be loaded for this project.');
    }
  }

  private applyIssuedAnswers(answers: AnswerViewModel[]): void {
    // For Issued status we render questions from answers payload
    // using groupName + questionText + comments from API response.
    const nextAnswerIdByQuestionId = new Map<number, number>();
    let displayNumber = 1;

    const issuedQuestions: QuestionnaireUiItem[] = answers.map(answer => {
      const questionId = typeof answer.questionId === 'number' && answer.questionId > 0
        ? answer.questionId
        : -(answer.id || displayNumber);

      if (answer.id > 0) {
        nextAnswerIdByQuestionId.set(questionId, answer.id);
      }

      const attachmentPath = this.questionnaireApi.normalizeAttachmentPath(answer.attachmentPath);
      const attachmentName = (answer.attachmentName ?? '').trim();
      const hasPersistedAttachment = !!attachmentPath;
      const fileName = hasPersistedAttachment
        ? (attachmentName && attachmentName.toLowerCase() !== 'no attachment'
          ? attachmentName
          : this.getAttachmentFileName(attachmentPath))
        : '';

      return {
        id: questionId,
        text: (answer.questionText ?? '').trim() || `Question ${displayNumber}`,
        comment: answer.comments ?? '',
        fileName,
        isMandatory: false,
        hasAttachment: hasPersistedAttachment,
        isMandatoryAttachment: false,
        attachmentPath,
        displayNumber: displayNumber++,
        groupName: (answer.groupingName ?? '').trim() || null,
      };
    });

    this.questions.set(issuedQuestions);
    this.answerIdByQuestionId = nextAnswerIdByQuestionId;
  }

  private applyAnswers(answers: AnswerViewModel[]): void {
    // Match strictly by questionId to avoid carrying values between classes
    // where different questions can share the same text.
    const byQuestionId = new Map<number, AnswerViewModel>(
      answers
        .filter((answer): answer is AnswerViewModel & { questionId: number } => typeof answer.questionId === 'number')
        .map(answer => [answer.questionId, answer]),
    );

    const nextAnswerIdByQuestionId = new Map<number, number>();

    this.questions.update(list =>
      list.map(item => {
        const answer = byQuestionId.get(item.id);
        if (!answer) {
          return item;
        }

        if (answer.id > 0) {
          nextAnswerIdByQuestionId.set(item.id, answer.id);
        }

        const attachmentPath = this.questionnaireApi.normalizeAttachmentPath(answer.attachmentPath);
        const attachmentName = (answer.attachmentName ?? '').trim();
        const hasPersistedAttachment = !!attachmentPath;
        const fileName = hasPersistedAttachment
          ? (attachmentName && attachmentName.toLowerCase() !== 'no attachment'
            ? attachmentName
            : this.getAttachmentFileName(attachmentPath))
          : '';

        return {
          ...item,
          comment: answer.comments ?? '',
          attachmentPath,
          fileName,
        };
      }),
    );

    this.answerIdByQuestionId = nextAnswerIdByQuestionId;
  }

  private async persistAnswers(options: { draft?: boolean } = {}): Promise<boolean> {
    const isDraft = options.draft === true;
    this.saveMessage.set(null);
    this.apiError.set(null);
    this.lastPersistRequestType = 'none';

    const projectId = this.projectId();

    if (!projectId) {
      this.saveMessage.set('Backend save skipped: projectId is not provided in route query params.');
      return false;
    }

    await this.uploadPendingFiles();

    if (!isDraft && this.hasMissingMandatoryPersistedAttachments()) {
      this.apiError.set('Attachment is required for mandatory questions. Please upload attachment and try again.');
      return false;
    }

    const answers: AnswerDto[] = this.questions()
      .map(item => {
        const comments = item.comment.trim();
        const attachmentPath = this.questionnaireApi.normalizeAttachmentPath(item.attachmentPath);
        const attachmentName = this.buildAttachmentName(item.fileName, attachmentPath);
        return {
          questionId: item.id,
          comments: comments || '',
          attachmentPath: attachmentPath || '',
          attachmentName,
        };
      })
      // Keep cleared answers that already exist so removed comments/attachments are persisted too.
      .filter(item => !!item.comments || !!item.attachmentPath || this.answerIdByQuestionId.has(item.questionId));

    const hasAllCurrentAnswerIds = this.questions().every(item => this.answerIdByQuestionId.has(item.id));
    const shouldUpdate = this.hasExistingAnswers() && hasAllCurrentAnswerIds;

    const updateAnswers: UpdateAnswerDto[] = this.questions()
      .map(item => {
        const comments = item.comment.trim();
        const attachmentPath = this.questionnaireApi.normalizeAttachmentPath(item.attachmentPath);
        const attachmentName = this.buildAttachmentName(item.fileName, attachmentPath);
        return {
          answerId: this.answerIdByQuestionId.get(item.id) ?? 0,
          comments: comments || '',
          attachmentPath: attachmentPath || '',
          attachmentName,
        };
      })
      .filter(item => item.answerId > 0);

    if (!answers.length && !shouldUpdate) {
      // Nothing to persist in questionnaire answers, but this should not block submit/navigation flow.
      this.saveMessage.set('No questionnaire answers to save.');
      this.isDirty.set(this.pendingFiles.size > 0);
      return true;
    }

    this.isSaving.set(true);
    try {
      if (shouldUpdate) {
        const updatePayload = {
          projectId,
          answers: updateAnswers,
        };
        await firstValueFrom(this.questionnaireApi.updateAnswers(updatePayload));
        this.lastPersistRequestType = 'put';
      } else {
        // Fallback to POST when question set changed (e.g., class changed after project management type update).
        const savePayload = { projectId, answers };
        await firstValueFrom(this.questionnaireApi.saveAnswers(savePayload));
        this.hasExistingAnswers.set(true);
        this.lastPersistRequestType = 'post';
        await this.refreshAnswerIds(projectId);
      }

      await this.deleteMarkedAttachments();

      this.isDirty.set(this.pendingFiles.size > 0);
      this.saveMessage.set('Questionnaire answers were sent to backend.');
      return true;
    } catch (error) {
      console.error('Failed to save answers:', error);
      if (this.isNotFoundError(error)) {
        this.apiError.set('Questionnaire answers were not saved because the project was not found.');
      }
      this.saveMessage.set('Failed to save answers to backend.');
      return false;
    } finally {
      this.isSaving.set(false);
    }
  }

  private async refreshAnswerIds(projectId: number): Promise<void> {
    try {
      const answers = await firstValueFrom(this.questionnaireApi.getAnswersByProject(projectId));
      const next = new Map<number, number>();
      for (const answer of answers) {
        if (typeof answer.questionId === 'number' && answer.id > 0) {
          next.set(answer.questionId, answer.id);
        }
      }
      this.answerIdByQuestionId = next;
    } catch (error) {
      // Answers are already saved; stale ids only mean the next save falls back to the idempotent POST upsert.
      console.error(error);
    }
  }

  private async deleteMarkedAttachments(): Promise<void> {
    if (!this.pendingAttachmentDeletes.size) {
      return;
    }

    for (const attachmentPath of [...this.pendingAttachmentDeletes]) {
      try {
        await this.questionnaireApi.deleteAttachment(attachmentPath);
        this.pendingAttachmentDeletes.delete(attachmentPath);
      } catch (error) {
        console.error(error);
        // Keep pending delete marker for a later retry and surface a non-blocking warning.
        this.apiError.set('Answers were saved, but one or more files could not be deleted from server.');
      }
    }
  }

  private async uploadPendingFiles(): Promise<void> {
    for (const [questionId, file] of this.pendingFiles.entries()) {
      try {
        const attachmentPath = await this.questionnaireApi.uploadAttachment(file);
        this.questions.update(list =>
          list.map(item =>
            item.id === questionId ? { ...item, attachmentPath } : item,
          ),
        );
        this.pendingFiles.delete(questionId);
      } catch (error) {
        console.error(error);
        this.apiError.set(`Failed to upload file "${file.name}". Saving without attachment.`);
      }
    }
  }

  private isMandatoryAttachmentMissingBeforeUpload(question: QuestionnaireUiItem): boolean {
    if (!question.isMandatoryAttachment) {
      return false;
    }

    const attachmentPath = this.questionnaireApi.normalizeAttachmentPath(question.attachmentPath);
    return !attachmentPath && !this.pendingFiles.has(question.id);
  }

  private hasMissingMandatoryPersistedAttachments(): boolean {
    return this.questions().some(question => {
      if (!question.isMandatoryAttachment) {
        return false;
      }

      const attachmentPath = this.questionnaireApi.normalizeAttachmentPath(question.attachmentPath);
      return !attachmentPath;
    });
  }

  private getAttachmentFileName(attachmentPath: string): string {
    const normalizedAttachmentPath = this.questionnaireApi.normalizeAttachmentPath(attachmentPath);
    const pathParts = normalizedAttachmentPath.split('/');
    return pathParts[pathParts.length - 1] || normalizedAttachmentPath;
  }

  private buildAttachmentName(fileName: string, attachmentPath: string): string {
    const normalizedFileName = fileName.trim();
    if (normalizedFileName) {
      return normalizedFileName;
    }

    if (attachmentPath) {
      return this.getAttachmentFileName(attachmentPath);
    }

    // Backend requires AttachmentName even when no file is attached.
    return 'No Attachment';
  }

  private async submitProject(projectId: number): Promise<boolean> {
    const draft = this.state.draftForm();
    const toIsoDate = (value: string): string | null => {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const date = new Date(trimmed);
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date.toISOString();
    };

    const projectCompletionDate = toIsoDate(draft.anticipatedCompletionDate);
    const deadlineDate = toIsoDate(draft.preliminaryCostDeadline);

    if (!projectCompletionDate || !deadlineDate) {
      this.apiError.set('Project completion and deadline dates are required before submit.');
      return false;
    }

    const dictionaryIds = await this.resolveProjectDictionaryIds();
    if (!dictionaryIds) {
      return false;
    }

    const payload = {
      id: projectId,
      projectTeam: draft.projectTeam,
      afeNumber: draft.afeNumber,
      projectTitle: draft.projectTitle,
      estimateTitle: draft.estimateTitle,
      projectNumber: draft.projectNumber,
      projectCode: draft.projectCode,
      estimateRequestTypeId: dictionaryIds.estimateRequestTypeId,
      projectManagementTypeId: dictionaryIds.projectManagementTypeId,
      cpdepPhaseId: dictionaryIds.cpdepPhaseId,
      designProgressId: dictionaryIds.designProgressId,
      projectCompletionDate,
      deadlineDate,
      areaId: dictionaryIds.areaId,
      unitId: dictionaryIds.unitId,
      ibpYearId: dictionaryIds.ibpYearId,
      turnaroundYearId: dictionaryIds.turnaroundYearId,
    };

    this.isSaving.set(true);
    try {
      await firstValueFrom(this.projectsApi.submitProject(projectId, payload));
      return true;
    } catch (error) {
      console.error('Failed to submit project:', error);
      if (this.isNotFoundError(error)) {
        this.apiError.set('Project was not submitted because it was not found.');
      }
      return false;
    } finally {
      this.isSaving.set(false);
    }
  }

  private isNotFoundError(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status === 404;
  }

  private async resolveProjectDictionaryIds(): Promise<{
    estimateRequestTypeId: number;
    projectManagementTypeId: number;
    cpdepPhaseId: number;
    designProgressId: number;
    areaId: number;
    unitId: number;
    ibpYearId: number;
    turnaroundYearId: number;
  } | null> {
    const draft = this.state.draftForm();
    const [estimateRequestTypes, projectManagementTypes, cpdepPhases, designProgresses, areas, units, ibpYears, turnaroundYears] = await Promise.all([
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('EstimateRequestTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('ProjectManagementTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('CpdepPhases')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('DesignProgresses')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Areas')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Units')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('IbpYears')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('TurnaroundYears')),
    ]);

    const ids = {
      estimateRequestTypeId: this.dictionariesApi.findDictionaryId(estimateRequestTypes, draft.estimateRequestType),
      projectManagementTypeId: this.dictionariesApi.findDictionaryId(projectManagementTypes, draft.projectManagementType),
      cpdepPhaseId: this.dictionariesApi.findDictionaryId(cpdepPhases, draft.cpdepPhase),
      designProgressId: this.dictionariesApi.findDictionaryId(designProgresses, draft.designProgress),
      areaId: this.dictionariesApi.findDictionaryId(areas, draft.area),
      unitId: this.dictionariesApi.findDictionaryId(units, draft.unit),
      ibpYearId: this.dictionariesApi.findDictionaryId(ibpYears, draft.ibp),
      turnaroundYearId: this.dictionariesApi.findDictionaryId(turnaroundYears, draft.taYear),
    };

    if (Object.values(ids).some(id => id <= 0)) {
      this.apiError.set('One or more dictionary values are outdated. Please return to project details and reselect dropdown values.');
      return null;
    }

    return ids;
  }
}

